/*
 * Sous composants de la section admin des emails de relance :
 * en tete avec interrupteur, delais, carte d'un email, bloc annulation,
 * dialog d'apercu. Les identifiants de test restent inchanges.
 */

import { Eye, MailPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const inputClass = "mt-1.5 min-h-[44px] rounded-xl border-[rgba(35,82,119,0.18)]";

export const RELANCE_LABELS = ["Première relance", "Deuxième relance", "Troisième relance"];

export const PreviewButton = ({ subject, body, testId, disabled, onPreview }) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    disabled={disabled}
    onClick={() => onPreview(subject, body)}
    data-testid={testId}
    className="rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-medium transition-colors duration-200"
  >
    <Eye className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
    Aperçu
  </Button>
);

export const RelanceHeader = ({ enabled, onPatch }) => (
  <div className="flex items-start justify-between gap-3 flex-wrap">
    <div>
      <div className="flex items-center gap-2 text-[#235277]">
        <MailPlus className="w-4 h-4" aria-hidden="true" />
        <h2 className="font-heading text-base font-semibold text-[#060606]">Emails de relance</h2>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
        Trois rappels doux après l'envoi du bilan, pour inviter à réserver un échange.
        Les relances s'arrêtent automatiquement dès qu'un rendez vous est enregistré ou
        si la personne se désinscrit. Chaque email contient un lien de désinscription.
      </p>
    </div>
    <div className="flex items-center gap-2">
      <Label htmlFor="relance-enabled" className="text-xs font-medium text-[#333333]">
        {enabled ? "Activées" : "Désactivées"}
      </Label>
      <Switch
        id="relance-enabled"
        data-testid="admin-relance-enabled-switch"
        checked={enabled}
        onCheckedChange={(value) => onPatch({ enabled: Boolean(value) })}
        className="data-[state=checked]:bg-[#235277]"
      />
    </div>
  </div>
);

export const RelanceDelaysBlock = ({ delays, onPatchDelay }) => (
  <div>
    <Label className="text-sm font-medium text-[#060606]">Délais d'envoi après le bilan (en jours)</Label>
    <div className="mt-1.5 grid grid-cols-3 gap-3">
      {delays.map((delay, index) => (
        <div key={`delai-${RELANCE_LABELS[index]}`}>
          <Input
            type="number"
            min="1"
            data-testid={`admin-relance-delay-${index + 1}-input`}
            value={delay}
            onChange={(e) => onPatchDelay(index, e.target.value)}
            className="min-h-[44px] rounded-xl border-[rgba(35,82,119,0.18)]"
          />
          <p className="mt-1 text-[11px] text-[#9E9E9E]">{RELANCE_LABELS[index]}</p>
        </div>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-[#9E9E9E]">
      Les délais doivent être croissants. Valeurs validées : 7, 14 et 21 jours.
    </p>
  </div>
);

export const RelanceEmailCard = ({ email, index, delay, previewLoading, onPreview, onPatchEmail }) => (
  <div className="rounded-xl border border-[rgba(35,82,119,0.14)] p-4 space-y-3">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="font-heading text-sm font-semibold text-[#235277]">
        {RELANCE_LABELS[index]} · jour {delay || "?"}
      </p>
      <PreviewButton
        subject={email.subject}
        body={email.body}
        testId={`admin-relance-preview-${index + 1}-button`}
        disabled={previewLoading}
        onPreview={onPreview}
      />
    </div>
    <div>
      <Label htmlFor={`relance-subject-${index}`} className="text-xs font-medium text-[#060606]">Sujet</Label>
      <Input
        id={`relance-subject-${index}`}
        data-testid={`admin-relance-subject-${index + 1}-input`}
        value={email.subject}
        onChange={(e) => onPatchEmail(index, "subject", e.target.value)}
        className={inputClass}
      />
    </div>
    <div>
      <Label htmlFor={`relance-body-${index}`} className="text-xs font-medium text-[#060606]">Corps du message</Label>
      <Textarea
        id={`relance-body-${index}`}
        data-testid={`admin-relance-body-${index + 1}-textarea`}
        value={email.body}
        onChange={(e) => onPatchEmail(index, "body", e.target.value)}
        rows={5}
        className="mt-1.5 rounded-xl border-[rgba(35,82,119,0.18)] text-sm leading-relaxed"
      />
    </div>
  </div>
);

export const RelanceCancellationBlock = ({ cancellation, previewLoading, onPreview, onPatchCancellation }) => (
  <div className="rounded-xl border border-[rgba(94,85,21,0.25)] bg-[rgba(253,246,231,0.45)] p-4 space-y-3" data-testid="admin-relance-cancellation-block">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <p className="font-heading text-sm font-semibold text-[#5E5515]">Rendez vous annulé</p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-[#9E9E9E]">
          Email doux envoyé une seule fois après une annulation, pour proposer un
          nouveau créneau. Jamais envoyé après une désinscription.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <PreviewButton
          subject={cancellation.subject}
          body={cancellation.body}
          testId="admin-relance-preview-cancellation-button"
          disabled={previewLoading}
          onPreview={onPreview}
        />
        <Switch
          data-testid="admin-relance-cancellation-switch"
          checked={cancellation.enabled}
          onCheckedChange={(value) => onPatchCancellation("enabled", Boolean(value))}
          className="data-[state=checked]:bg-[#235277]"
        />
      </div>
    </div>
    <div>
      <Label htmlFor="relance-cancel-subject" className="text-xs font-medium text-[#060606]">Sujet</Label>
      <Input
        id="relance-cancel-subject"
        data-testid="admin-relance-cancellation-subject-input"
        value={cancellation.subject}
        onChange={(e) => onPatchCancellation("subject", e.target.value)}
        className={inputClass}
      />
    </div>
    <div>
      <Label htmlFor="relance-cancel-body" className="text-xs font-medium text-[#060606]">Corps du message</Label>
      <Textarea
        id="relance-cancel-body"
        data-testid="admin-relance-cancellation-body-textarea"
        value={cancellation.body}
        onChange={(e) => onPatchCancellation("body", e.target.value)}
        rows={4}
        className="mt-1.5 rounded-xl border-[rgba(35,82,119,0.18)] text-sm leading-relaxed"
      />
    </div>
  </div>
);

export const RelancePreviewDialog = ({ preview, onClose }) => (
  <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-2xl rounded-2xl" data-testid="admin-relance-preview-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-base text-[#060606]">
          Aperçu de l'email
        </DialogTitle>
        <DialogDescription className="text-xs text-[#9E9E9E]">
          Sujet : {preview?.subject}. Rendu identique à l'email réel, avec des données d'exemple.
        </DialogDescription>
      </DialogHeader>
      <iframe
        title="Aperçu de l'email de rappel"
        srcDoc={preview?.html || ""}
        sandbox=""
        data-testid="admin-relance-preview-frame"
        className="w-full h-[480px] rounded-xl border border-[rgba(35,82,119,0.18)] bg-white"
      />
    </DialogContent>
  </Dialog>
);
